// Tabela visual dos tiers de VIP (dados puros — nenhum Vue, nenhuma store).
//
// Fonte: Figma "1. Página Principal - Aposta Tudo", component set do card da
// roletinha por tier (node 21791:67598). Ver scripts/loyalty-cards.figma.md.
//
// A chave é o `order` do nível no core (VipLevelSeeder), que vai de 0 a 7. As
// artes cobrem 0..6; o 7 (SIGNATURE) NÃO tem desenho e por isso cai no card
// estático padrão — ver a nota no fim do arquivo. Este é o arquivo que cresce
// quando ele ganhar arte: uma entrada aqui + o badge e a roda em
// public/assets/images/loyalty-hub/vip/. Nada de Vue muda.
//
// As CORES daqui são do Figma e não do `color` que o core manda no nível: as duas
// paletas divergem de propósito (o VIP 3 é verde no card e violeta no
// backoffice). Trocar a cor de um nível no backoffice não mexe no card.

import { getAssetsUrlForCDN } from '@/utils/template';

const VIP_ART_BASE = '/assets/images/loyalty-hub/vip';

export interface VipTierStyle {
  // Rótulo textual do tier — entra no `alt`/leitor de tela, já que na tela ele
  // aparece como imagem (badge) e dentro do título.
  label: string;
  // Paradas do gradiente do card. As duas cores vêm de tokens do DS; o ângulo e
  // as porcentagens são iguais em todos os tiers (ver RouletteVipCard.vue).
  from: string;
  to: string;
  // Caminhos locais (`/assets/...`). Quem consome recebe eles já passados pelo CDN
  // de estáticos — ver `vipTierStyle` no fim do arquivo.
  badge: string;
  wheel: string;
}

// O tier 0 não escreve número no card ("Roleta Diária VIP", e não "VIP 0") — foi
// assim que o design fechou. Por isso `label` é dado, não `\`VIP ${tier}\``.
export const VIP_TIERS: Record<number, VipTierStyle> = {
  0: {
    label: 'VIP',
    from: '#000000', // colors/black
    to: '#94a3b8', // colors/slate/400
    badge: `${VIP_ART_BASE}/badge-0.webp`,
    wheel: `${VIP_ART_BASE}/wheel-0.webp`,
  },
  1: {
    label: 'VIP 1',
    from: '#18181b', // colors/eclipse
    to: '#eef2ff', // colors/indigo/50
    badge: `${VIP_ART_BASE}/badge-1.webp`,
    wheel: `${VIP_ART_BASE}/wheel-1.webp`,
  },
  2: {
    label: 'VIP 2',
    from: '#713f12', // colors/yellow/900
    to: '#eab308', // colors/yellow/500
    badge: `${VIP_ART_BASE}/badge-2.webp`,
    wheel: `${VIP_ART_BASE}/wheel-2.webp`,
  },
  3: {
    label: 'VIP 3',
    from: '#052e16', // colors/green/950
    to: '#15803d', // colors/green/700
    badge: `${VIP_ART_BASE}/badge-3.webp`,
    wheel: `${VIP_ART_BASE}/wheel-3.webp`,
  },
  4: {
    label: 'VIP 4',
    from: '#090963', // colors/blue/800
    to: '#1414dd', // colors/blue/500
    badge: `${VIP_ART_BASE}/badge-4.webp`,
    wheel: `${VIP_ART_BASE}/wheel-4.webp`,
  },
  5: {
    label: 'VIP 5',
    from: '#450a0a', // colors/red/950
    to: '#ef4444', // colors/red/500
    badge: `${VIP_ART_BASE}/badge-5.webp`,
    wheel: `${VIP_ART_BASE}/wheel-5.webp`,
  },
  6: {
    label: 'VIP 6',
    from: '#090963', // colors/blue/800
    to: '#22d3ee', // colors/cyan/400
    // Este badge NÃO saiu da arte de origem como os outros: a instância dele no
    // Figma aponta pra um desenho diferente (coroa alta em quadro quadrado,
    // inserida esticada), então foi extraído do card já RENDERIZADO, resolvendo
    // a cor contra o gradiente conhecido. Ver scripts/loyalty-cards.figma.md.
    badge: `${VIP_ART_BASE}/badge-6.webp`,
    wheel: `${VIP_ART_BASE}/wheel-6.webp`,
  },
};

// Estilo do tier, ou `undefined` quando não existe tratamento pra ele: tier
// `null` (jogador comum, ou payload sem `order`) e tier fora da faixa desenhada.
// Quem chama usa o `undefined` pra cair no card padrão.
//
// É AQUI que o SIGNATURE (order 7) cai hoje, e é uma lacuna consciente, não um
// bug: o nível mais alto da casa fica com o card estático até ganhar arte
// própria. NÃO resolver isso com `Math.min(order, 6)` — o tier de convite
// desenhado igual ao VIP 6 é pior que o card neutro.
//
// As artes saem daqui já resolvidas pelo CDN de estáticos, como as dos outros
// cards do hub (`loyaltyHubCardArt`). A resolução é NESTA função e não na tabela
// porque `getAssetsUrlForCDN` lê `useRuntimeConfig()` — em tempo de módulo ele cai
// no fallback e o caminho sai local.
export function vipTierStyle(tier?: number | null): VipTierStyle | undefined {
  if (tier === null || tier === undefined) return undefined;

  const style = VIP_TIERS[tier];

  if (!style) return undefined;

  return {
    ...style,
    badge: getAssetsUrlForCDN(style.badge),
    wheel: getAssetsUrlForCDN(style.wheel),
  };
}
